import {
  FETCH_FLOWCODES_QUERY,
  GET_DIRECTORY_QUERY,
  GET_FLOWCODE_DIRECTORIES_QUERY,
  GET_WHITE_LABEL_REDIRECT_DOMAINS_QUERY
} from '../../gql/queries/flowcode'
import {
  FetchFlowcodesQueryResponseType,
  FetchFlowcodesVariablesType,
  FetchFoldersVariablesType
} from '../../types/code-management.types'
import { FetchFolderQueryResponseType, FetchFolderVariablesType } from '../../types/folder.types'
import { FetchFoldersType } from '../../types/hooks.types'
import { GET_NESTED_DIRECTORIES } from '@dtx-company/flow-codegen/src/code/queries'
import {
  GetAllNestedDirectoriesResponseType,
  GetFlowcodeDirectoriesResponseType,
  GetWhiteLabelRedirectDomainsResponseType
} from '../../types/flowcode.types'
import { getFetchFolderKey } from '../../utils/folders'
import { gqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useShowDomainsPage } from '@app/common/src/hooks/useShowDomainsPage'
import useSWR from 'swr'
import useSWRInfinite, { SWRInfiniteResponse } from 'swr/infinite'

export const getFetchFlowcodesKey = (
  pageIndex: number,
  previousPageData: FetchFlowcodesQueryResponseType | null,
  variables: FetchFlowcodesVariablesType,
  token?: string
): string[] | null => {
  const key = pageIndex.toString()
  const pageInfo = previousPageData?.myFlowcodes?.allBatches?.pageInfo
  if ((previousPageData && !pageInfo?.hasNextPage) || !token) {
    return null
  }

  if (pageIndex === 0 || !pageInfo) {
    return [
      key,
      '',
      token,
      variables.orderHighlightedBy || '',
      variables.orderBy || '',
      variables.contains || '',
      variables.withStudioConfig?.toString() ?? ''
    ]
  }

  return [
    key,
    pageInfo.endCursor,
    token,
    variables.orderHighlightedBy || '',
    variables.orderBy || '',
    variables.contains || '',
    variables.withStudioConfig?.toString() ?? ''
  ]
}

// TODO - update return type to use new interfaces exported by SWR - DOM

/**
 * Hook used to fetch flowcodes data for the code management page
 */
export const useFetchFlowcodes = (
  isForFolder: boolean,
  variables: FetchFlowcodesVariablesType = {},
  token?: string
): SWRInfiniteResponse<FetchFlowcodesQueryResponseType> => {
  const data = useSWRInfinite(
    (
      pageIndex: number,
      previousPageData: FetchFlowcodesQueryResponseType | null
    ): string[] | null => {
      return getFetchFlowcodesKey(pageIndex, previousPageData, variables, token)
    },
    ([_, cursor, token]) =>
      gqlFetcher<FetchFlowcodesQueryResponseType>(
        FETCH_FLOWCODES_QUERY,
        {
          after: cursor,
          ...variables
        },
        undefined,
        token
      ),
    {
      revalidateOnFocus: false
    }
  )
  return data
}

/**
 * Hook used to fetch folders data for the code management page
 */
export const useFetchFolders = (
  variables: FetchFoldersVariablesType = {},
  enableFetch = true
): FetchFoldersType => {
  const { token } = useAuthState()
  const data = useSWR(
    () =>
      enableFetch && token
        ? [
            GET_FLOWCODE_DIRECTORIES_QUERY,
            token,
            variables.orderBy,
            variables.contains,
            variables.parentDirectoryId
          ]
        : null,
    ([query, token]) =>
      gqlFetcher<GetFlowcodeDirectoriesResponseType>(
        query,
        {
          ...variables
        },
        undefined,
        token
      ),
    {
      revalidateOnFocus: false
    }
  )

  return {
    ...data,
    isLoading: !data.error && !data.data
  }
}

export const useFetchFolder = (
  variables: FetchFolderVariablesType,
  token?: string
): SWRInfiniteResponse<FetchFolderQueryResponseType> => {
  const data = useSWRInfinite(
    (pageIndex: number, previousPageData: FetchFolderQueryResponseType | null): string[] | null => {
      return getFetchFolderKey(pageIndex, previousPageData, variables, token)
    },
    ([_, cursor, token]) =>
      gqlFetcher<FetchFolderQueryResponseType>(
        GET_DIRECTORY_QUERY,
        {
          after: cursor,
          ...variables
        },
        undefined,
        token
      ),
    {
      revalidateOnFocus: false
    }
  )
  return data
}

// useTryFetchFolder fetches the directory if the directoryId is truthy,
// otherwise it just returns empty placeholder values.
// This is to get around the problem where React doesn't allow us to
// conditionally call a hook since that changes the number of hooks called.
export const useTryFetchFolder = (
  variables: FetchFolderVariablesType,
  directoryId?: string,
  token?: string
): SWRInfiniteResponse<FetchFolderQueryResponseType> => {
  const data = useSWRInfinite<FetchFolderQueryResponseType, Error>(
    (pageIndex: number, previousPageData: FetchFolderQueryResponseType | null): any => {
      return getFetchFolderKey(pageIndex, previousPageData, variables, token)
    },
    (_: string, cursor: string, token: string): any => {
      const emptyFolderResponse = {
        directory: {
          analytics: {
            total: {
              scans: 0
            }
          },
          level: 0,
          id: '',
          name: '',
          ancestors: [],
          highlighted: {
            edges: []
          },
          batchCount: 0,
          isOwner: false,
          entityPermissions: [],
          user: {
            fullName: '',
            ithacaId: ''
          }
        },
        myFlowcodes: {
          allBatches: {
            edges: [],
            pageInfo: {
              startCursor: '',
              endCursor: '',
              hasNextPage: false
            }
          }
        }
      }

      if (directoryId) {
        return gqlFetcher(
          GET_DIRECTORY_QUERY,
          {
            after: cursor,
            ...variables
          },
          undefined,
          token
        )
      } else {
        return emptyFolderResponse
      }
    },
    {
      revalidateOnFocus: false
    }
  )
  return data
}

/**
 * Hook used to fetch white label redirect domain options that the user has access to.
 */
export const useFetchWhiteLabelRedirectDomains = (
  token = '',
  withTimestamp = false
): {
  whiteLabelDomains?: GetWhiteLabelRedirectDomainsResponseType
  error: Error | null
  isLoading: boolean
} => {
  const showDomainsPage = useShowDomainsPage()
  const data = useSWR(
    () => (token && showDomainsPage ? [GET_WHITE_LABEL_REDIRECT_DOMAINS_QUERY, token] : null),
    ([query, token]) =>
      gqlFetcher<GetWhiteLabelRedirectDomainsResponseType>(
        query,
        {
          withTimestamp
        },
        undefined,
        token
      ),
    {
      revalidateOnFocus: false
    }
  )

  return {
    whiteLabelDomains: data.data,
    error: data.error,
    isLoading: !data.error && !data.data
  }
}

export const useFetchNestedDirectories = (
  token = ''
): { allDirectories?: GetAllNestedDirectoriesResponseType; isLoading: boolean } => {
  const { data, isLoading } = useSWR(
    () => (token ? [GET_NESTED_DIRECTORIES, token] : null),
    ([query, token]) =>
      gqlFetcher<GetAllNestedDirectoriesResponseType>(query, undefined, false, token),
    {}
  )

  return { allDirectories: data, isLoading }
}
